<template>
  <div>
    <transition name="fade">
      <div class="be-maplegacy__dim" v-if="isOpen" @click="close"></div>
    </transition>

    <transition name="fade">
      <div class="be-maplegacy__wrap" v-if="isOpen">
        <div class="be-maplegacy__panel">
          <div class="be-maplegacy__head">
            <!-- taken from the map pin, so the panel has a title from the
                 first frame instead of waiting on the search request -->
            <p class="be-maplegacy__title mb-0 text-truncate">{{ roomTypeName }}</p>
            <img
              src="../../../../assets/img/icon/cancel.png"
              class="be-maplegacy__close d-inline-block p-2 rounded-3 bg-back cursor-pointer"
              width="33"
              alt=""
              @click="close">
          </div>

          <div class="be-maplegacy__body">
            <div class="text-center py-5" v-if="is_loading">
              <b-spinner variant="secondary"></b-spinner>
            </div>

            <template v-else-if="room_type">
              <!-- The room IS sold here, it is only gone for these dates, so
                   alternatives are worth offering. Keyed on the stay dates: the
                   carousel fetches once on mount and reads the filter from the
                   store, so a reload on new dates has to remount it or it would
                   keep showing the old suggestions. -->
              <FlexibleDateAlternatives
                v-if="isRoomSoldOut"
                class="mb-3"
                :key="flexibleDatesKey"
                :room-type-id="roomTypeId"
                :title="$t('hotel.map-flexible-dates-title')"
                @select-date="$emit('select-date', $event)" />

              <BeRoomDesktop
                v-if="!isMobile"
                @room_type_detail="$emit('room_type_detail', $event)"
                @toggleRateBreakdown="(type, data) => $emit('toggleRateBreakdown', type, data)"
                @toggleBookingRate="(data) => $emit('toggleBookingRate', data)"
                @toggleBookingRateCart="onDesktopBookingRateCart"
                @handlePopupFilter="$emit('handlePopupFilter', $event)"
                :rt_hightlight_index="rtHightlightIndex"
                :roomRateHighlight="roomRateHighlight"
                :type="room_type"
                :type_index="0"
                :type_avlb="getRoomAvlb(room_type.id)"
                :header_scroll="headerScroll"
                :is_booking_dropdown="isBookingDropdown"
                :show_rate="showRate"
                :show_rate_breakdown="showRateBreakdown"
                :is_active="isActive"
                :formatDate="formatDate"
                :totalRateBreakdown="totalRateBreakdown"
                :popup_active="popupActive">
              </BeRoomDesktop>

              <BeRoomMobile
                v-else
                @room_type_detail="$emit('room_type_detail', $event)"
                @room_rate_detail="$emit('room_rate_detail', $event)"
                @room_rate_add="$emit('room_rate_add', $event)"
                @more_room="$emit('more_room', $event)"
                :rt_hightlight_index="rtHightlightIndex"
                :roomRateHighlight="roomRateHighlight"
                :data="room_type"
                :type_index="0"
                :type_avlb="getRoomAvlb(room_type.id)"
                :header_scroll="headerScroll"
                :selected_room_rate="selectedRoomRate"
                :type="popupActive"
                :designType="designType">
              </BeRoomMobile>
            </template>

            <!-- Not in the search results at all: the room is not on sale, which
                 no other date can fix -- show what the map knows about it and
                 leave it there. -->
            <BeMapRoomUnavailable
              v-else
              :room-type-id="roomTypeId"
              :merchant-id="searchParams.merchant_id"
              :message="error_message || $t('hotel.map-room-unavailable')" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { mapMutations } from 'vuex';
  import { get } from '@/services/index.js';
  import { withIndexedRateIds } from '@/utils/beRoomType.js';
  import BeRoomDesktop from '@/components/hotel/be/desktop/room';
  import BeRoomMobile from '@/components/hotel/be/mobile/room';
  import FlexibleDateAlternatives from '@/components/hotel/be/FlexibleDateAlternatives.vue';
  import BeMapRoomUnavailable from './BeMapRoomUnavailable.vue';

  export default {
    name: 'BeMapRoomModalLegacy',
    components: {
      BeRoomDesktop,
      BeRoomMobile,
      FlexibleDateAlternatives,
      BeMapRoomUnavailable
    },
    props: {
      roomTypeId: {
        type: [String, Number],
        default: null
      },
      // the pin's label, shown while the rates are still loading
      roomTypeName: {
        type: String,
        default: ''
      },
      // the page's current search-room params; room_type_id is added on top
      searchParams: {
        type: Object,
        default: () => ({})
      },
      isMobile: {
        type: Boolean,
        default: false
      },
      // every other prop below is a straight pass-through of the same
      // page-level UI state the room-card loop already binds -- the modal
      // never has more than one room type on screen at a time, so reusing it
      // is safe and needs no new state on the page
      getRoomAvlb: {
        type: Function,
        default: () => 0
      },
      headerScroll: {
        type: Boolean,
        default: false
      },
      rtHightlightIndex: {
        type: Number,
        default: -1
      },
      roomRateHighlight: {
        type: Object,
        default: () => ({})
      },
      // desktop-only
      isBookingDropdown: {
        type: [String, Boolean],
        default: false
      },
      showRate: {
        type: [String, Boolean],
        default: false
      },
      showRateBreakdown: {
        type: [String, Boolean],
        default: false
      },
      isActive: {
        type: String,
        default: ''
      },
      formatDate: {
        type: Function,
        default: () => ''
      },
      totalRateBreakdown: {
        type: Function,
        default: () => 0
      },
      // mobile-only
      selectedRoomRate: {
        type: Object,
        default: () => ({})
      },
      designType: {
        type: String,
        default: 'OLD'
      },
      // shared: desktop uses it as a boolean/string popup flag, mobile as a
      // status string ('room_rate' / 'room_rate_add' / 'close') -- both pages
      // already own this as `popup_active`, just forwarded through
      popupActive: {
        type: [String, Boolean],
        default: false
      }
    },
    data(){
      return {
        room_type: null,
        is_loading: false,
        error_message: ''
      }
    },
    computed: {
      isOpen(){
        return this.$isNotEmptyV2(this.roomTypeId);
      },
      /* One computed rather than a watcher per prop: the page sets both the
         params and the room type in the same tick when a popup opens, and two
         watchers would each fire a search for it. */
      loadSignature(){
        if(!this.$isNotEmptyV2(this.roomTypeId)) return '';
        return this.roomTypeId + '|' + JSON.stringify(this.searchParams);
      },
      flexibleDatesKey(){
        const { check_in_date, check_out_date } = this.searchParams;
        return this.roomTypeId + '_' + check_in_date + '_' + check_out_date;
      },
      /* Same rule the list view applies per page (isAllRoomsSoldOut), narrowed
         to the one room type this panel shows. A rate the guest cannot book --
         closed out or with nothing left -- counts as gone for these dates. */
      isRoomSoldOut(){
        if(!this.$isNotEmptyV2(this.room_type)) return false;
        const rates = this.room_type.room_rate || [];
        if(rates.length == 0) return true;
        return rates.every((rate) => !rate.bookable || this.$roomAvbl(rate.room_type.id, rate.id) <= 0);
      }
    },
    watch: {
      isOpen(isOpen){
        if(isOpen) this.lockPage();
        else this.unlockPage();
      },
      loadSignature: {
        immediate: true,
        handler(loadSignature){
          if(!loadSignature){
            this.room_type = null;
            this.error_message = '';
            return;
          }
          this.loadRoomType(this.roomTypeId);
        }
      }
    },
    created(){
      // plain instance property: restoring an inline style is not view state
      this.previous_body_overflow = '';
      // request sequence number, so a slow response for a popup the guest has
      // already closed cannot paint into the next one they open
      this.load_request_token = 0;
    },
    mounted(){
      if(this.isOpen) this.lockPage();
    },
    beforeDestroy(){
      this.unlockPage();
    },
    methods: {
      ...mapMutations(['mergeBeRoomAvbl']),
      /* Runs on every open, never from a cached snapshot: the map itself is
         built from room-type, which knows nothing about the guest's dates,
         so this filtered search is the only place real rates and availability
         for the picked room come from. */
      async loadRoomType(roomTypeId){
        this.load_request_token += 1;
        const requestToken = this.load_request_token;

        this.is_loading = true;
        this.room_type = null;
        this.error_message = '';

        try {
          const apiParams = Object.assign({}, this.searchParams, {
            room_type_id: roomTypeId,
          });
          const apiResponse = await get('CORE_HOTEL', 'search-room', apiParams);
          if(requestToken != this.load_request_token) return;

          const { success, message, data, room_type_availability } = apiResponse.data;
          if(!success) throw new Error(message);

          /* Merge, never replace: the payload only covers this one room type,
             and setBeRoomAvbl would drop the availability of every other one
             along with their cart rows. */
          const availability = (room_type_availability || []).map((roomType) => {
            roomType.original_availability = roomType.availability;
            return roomType;
          });
          this.mergeBeRoomAvbl(availability);

          this.room_type = data && data.length > 0 ? withIndexedRateIds(data[0]) : null;
        } catch (error) {
          if(requestToken != this.load_request_token) return;
          this.error_message = error.message;
        } finally {
          if(requestToken == this.load_request_token) this.is_loading = false;
        }
      },
      lockPage(){
        this.previous_body_overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
      },
      unlockPage(){
        document.body.style.overflow = this.previous_body_overflow || '';
        document.removeEventListener('keydown', this.onKeydown);
      },
      onKeydown(e){
        if(e.key === 'Escape' || e.key === 'Esc') this.close();
      },
      close(){
        this.$emit('close');
      },
      // desktop's toggleBookingRateCart(type, data, qty) unambiguously marks
      // a completed add -- mobile has no such signal (see mobile/roomRateDetail
      // .vue, which only emits a bare 'close' for both add and cancel), so only
      // the desktop branch auto-closes
      onDesktopBookingRateCart(type, data, qty){
        this.$emit('toggleBookingRateCart', type, data, qty);
        if(type == 'add') this.close();
      }
    }
  }
</script>

<style scoped>
/* Above every BE navbar (all of them sit at z-index 1040: HotelTopNavbar,
   desktop/navbar, BeLandingNavbar) or the header stays lit on top of the dim.
   Still below every legacy detail/rate popup on purpose (desktop
   roomTypeDetail is 1045; mobile roomTypeDetail/roomRateDetail go up to
   1043/1046) -- the "detail" button inside the card must open those on top. */
.be-maplegacy__dim {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1041;
  background: rgba(0, 0, 0, 0.5);
}
.be-maplegacy__wrap {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /* toggleBookingRate also flips the page-level PopUpBackground to
     z-index-1041 (its own qty-dropdown backdrop) -- panel must clear that
     or the dropdown renders visible-but-dimmed underneath it */
  z-index: 1042;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  pointer-events: none;
}
.be-maplegacy__panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1080px;
  max-height: 90vh;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
  overflow: hidden;
  pointer-events: auto;
}
.be-maplegacy__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e3e6eb;
}
.be-maplegacy__title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a2e;
}
.be-maplegacy__close {
  flex-shrink: 0;
}
.be-maplegacy__body {
  padding: 16px 20px;
  overflow-y: auto;
}
/* The room type card sticks against the page navbar (top: 185px, .top-90,
   .top-5). Inside this panel's own scroller those offsets only tear the card
   away from the rate column the moment Rate Detail makes it taller. */
.be-maplegacy__body >>> .sticky {
  position: static;
}
/* On the results page these cards sit on the grey .bg-light-desktop backdrop,
   where light-card-shadow alone reads as an edge. This panel is white, so the
   room type card and every rate row without the .border-2-green highlight
   (which checkRoomRateDetailInline also drops while its detail is expanded)
   would otherwise bleed into it. */
.be-maplegacy__body >>> .light-card-shadow {
  border: 1px solid #d5d9e0;
}
@media screen and (max-width: 767.98px) {
  .be-maplegacy__wrap {
    align-items: flex-end;
    padding: 0;
  }
  .be-maplegacy__panel {
    max-width: none;
    max-height: 92vh;
    border-radius: 16px 16px 0 0;
  }
  .be-maplegacy__head {
    padding: 14px 16px;
  }
  .be-maplegacy__body {
    padding: 12px 16px 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeMapRoomModalLegacy.vue